import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { ContractFormFieldService } from '../services/contract-form-field.service';
import { FormField } from '../shared/form-field/form-field';
import { Observable, Subscription, Subject } from 'rxjs';
import { startWith, takeUntil } from 'rxjs/operators';
import { RoleAuthorizationService } from '../../../services/role-authorization.service';
import { AzureLoginService } from '../../../services/azure-login.service';
import {
  IRoleAuthorizationService,
  AuthorizationType,
} from '../../../interfaces/role-authorization.interface';
import { SidebarStateService } from '../services/sidebar-state.service';
import { FeatureFlagsService } from '../services/feature-flags.service';
import { UserProfileTabService } from '../contracts/user-profile-tab/user-profile-tab.service';
import { SidebarSubMenuItem } from './sidebar-navigation';

import {
  CONTRACTSLIST,
  USER_PROFILE_FEATURE_FLAG,
  SERVICELIST,
  NOTIFICATIONLIST,
  BUYINGGROUPLIST,
  TROUBLESHOOTINGLIST,
  DEALERLOCATIONLIST,
  COVERAGELIST,
  CATALOGLIST,
  FORMLIST,
  CLAIMLIST,
  SALESREGISTERLIST,
  DOCUMENTLIST,
  SECURITYLIST,
  CONFIGURATIONLIST,
  REPORTSLIST,
} from './sidebar-navigation-constants';

const sidebarTitles = ['', 'Contractor', 'Dealer'];
@Component({
  standalone: false,
  selector: 'crm-sidebar',
  templateUrl: 'crm-sidebar.template.html',
  styleUrls: ['crm-sidebar.scss'],
  providers: [ContractFormFieldService, UserProfileTabService],
})
export class CrmSidebarComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  public strings: any;
  public sidebarCollapsed = false;
  public sidebarTitle = '';
  public selectedTab = 2;
  public contractsList = CONTRACTSLIST;
  public serviceList = SERVICELIST;
  public notificationList = NOTIFICATIONLIST;
  public buyingGroupList = BUYINGGROUPLIST;
  public troubleshootingList = TROUBLESHOOTINGLIST;
  public dealerLocationList = DEALERLOCATIONLIST;
  public coverageList = COVERAGELIST;
  public catalogList = CATALOGLIST;
  public formList = FORMLIST;
  public claimList = CLAIMLIST;
  public salesregisterlist = SALESREGISTERLIST;
  public documentList = DOCUMENTLIST;
  public securityList = SECURITYLIST;
  public configurationList = CONFIGURATIONLIST;
  public reportsList = REPORTSLIST;
  public authDetails: IRoleAuthorizationService;

  formFields$: Observable<FormField<any>[]>;
  public theBoundCallback: any;
  public selectedNavItemId: number;
  public CloseDialog: any;
  private sidebarStateSubscription: Subscription;
  private userProfileEligibilitySub: Subscription;
  private userProfileCheckId = 0;

  constructor(
    private router: Router,
    private currentActivatedRoute: ActivatedRoute,
    service: ContractFormFieldService,
    private roleService: RoleAuthorizationService,
    private sidebarStateService: SidebarStateService,
    private azureService: AzureLoginService,
    private featureFlagService: FeatureFlagsService,
    private userProfileTabService: UserProfileTabService,
    private cdr: ChangeDetectorRef,
  ) {
    this.formFields$ = service.getFormFields();
    // Initialize callback in constructor to prevent timing issues
    this.theBoundCallback = this.theCallback.bind(this);
    
    // Subscribe to sidebar state changes from other components
    this.sidebarStateSubscription = this.sidebarStateService.collapsed$.subscribe(
      (collapsed) => {
        this.sidebarCollapsed = collapsed;
      }
    );
  }

  ngOnInit() {
    this.azureService.rolePermissionsUpdated$
      .pipe(startWith(undefined), takeUntil(this.destroy$))
      .subscribe(() => this.applySidebarMenuAuthorization());
    this.featureFlagService.featureFlagsUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.applySidebarMenuAuthorization());
  }

  /**
   * Rebuilds every module submenu from fresh copies of the constants, then applies the same
   * authorization rules as before. Re-running after `rolePermissions` loads fixes intermittent
   * empty lists without mutating the shared constant arrays.
   */
  private applySidebarMenuAuthorization(): void {
    const allLists = [
      JSON.parse(JSON.stringify(CONTRACTSLIST)),
      JSON.parse(JSON.stringify(SERVICELIST)),
      JSON.parse(JSON.stringify(NOTIFICATIONLIST)),
      JSON.parse(JSON.stringify(BUYINGGROUPLIST)),
      JSON.parse(JSON.stringify(TROUBLESHOOTINGLIST)),
      JSON.parse(JSON.stringify(DEALERLOCATIONLIST)),
      JSON.parse(JSON.stringify(COVERAGELIST)),
      JSON.parse(JSON.stringify(CATALOGLIST)),
      JSON.parse(JSON.stringify(FORMLIST)),
      JSON.parse(JSON.stringify(CLAIMLIST)),
      JSON.parse(JSON.stringify(SALESREGISTERLIST)),
      JSON.parse(JSON.stringify(DOCUMENTLIST)),
      JSON.parse(JSON.stringify(SECURITYLIST)),
      JSON.parse(JSON.stringify(CONFIGURATIONLIST)),
      JSON.parse(JSON.stringify(REPORTSLIST)),
    ];
    for (let index = 0; index < allLists.length; index++) {
      this.authDetails = {
        authorizationType: AuthorizationType.LeftPanel,
        component: CrmSidebarComponent,
        componentName: 'CrmSidebarComponent',
        generalArray: allLists[index],
      };
      allLists[index] = Object.assign(this.roleService.applyAuthorization(this.authDetails));
    }
    for (let index = 0; index < allLists.length; index++) {
      allLists[index] = Object.assign(this.roleService.validateSideMenu(allLists[index]));
    }
    this.contractsList = allLists[0];
    this.applyUserProfileAccess();
    this.serviceList = allLists[1];
    this.notificationList = allLists[2];
    this.buyingGroupList = allLists[3];
    this.troubleshootingList = allLists[4];
    this.dealerLocationList = allLists[5];
    this.coverageList = allLists[6];
    this.catalogList = allLists[7];
    this.formList = allLists[8];
    this.claimList = allLists[9];
    this.salesregisterlist = allLists[10];
    this.documentList = allLists[11];
    this.securityList = allLists[12];
    this.configurationList = allLists[13];
    this.reportsList = allLists[14];
    this.cdr.detectChanges();
  }

  /**
   * Shows Contracts "User Profile" only when the UserProfile API flag is on
   * and getUserProfileTabEligibility returns true for this Azure user and role.
   * Other sidebar items keep the existing role-permission rules.
   */
  private applyUserProfileAccess(): void {
    const withoutProfile = this.contractsList.filter((item) => item.name !== 'User Profile');
    this.contractsList = withoutProfile;
    this.userProfileEligibilitySub?.unsubscribe();
    const checkId = ++this.userProfileCheckId;

    if (!this.featureFlagService.isFeatureFlagEnabled(USER_PROFILE_FEATURE_FLAG)) {
      return;
    }
    const azureUserId = this.azureService.getAccountIdValue();
    if (!azureUserId) {
      return;
    }
    this.userProfileEligibilitySub = this.userProfileTabService
      .checkUserProfileTabEligibility(azureUserId)
      .subscribe(
        ({ data }) => {
          if (checkId !== this.userProfileCheckId) {
            return;
          }
          if (data?.getUserProfileTabEligibility === true) {
            this.contractsList = this.withUserProfileItem(withoutProfile);
            this.cdr.detectChanges();
          }
        },
        () => {
          if (checkId !== this.userProfileCheckId) {
            return;
          }
          this.contractsList = withoutProfile;
        }
      );
  }

  private withUserProfileItem(contractsList: SidebarSubMenuItem[]): SidebarSubMenuItem[] {
    if (contractsList.some((item) => item.name === 'User Profile')) {
      return contractsList;
    }
    const profileItem = CONTRACTSLIST.find((item) => item.name === 'User Profile');
    if (!profileItem) {
      return contractsList;
    }
    return [...contractsList, JSON.parse(JSON.stringify(profileItem))];
  }

  public theCallback(id) {
    this.selectedNavItemId = id;
  }

  public toggleCollapse() {
    this.sidebarStateService.toggle();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.sidebarStateSubscription) {
      this.sidebarStateSubscription.unsubscribe();
    }
    if (this.userProfileEligibilitySub) {
      this.userProfileEligibilitySub.unsubscribe();
    }
  }
}
